import type { GlobalCopy, MakeTransferCopy } from '../../../core'
import type { SaveExtraBlockData } from '../../../core/types/InsightsInstanceSlotData'

/**
 * MakeTransfer drilldown variants. The block uses the VALUE (e.g. "SaveAnExtra100"),
 * which is also the key each variant registers under (see ./index).
 */
export const variants = {
  SAVE_ENOUGH_TO_LIVE_ON: 'SaveEnoughToLiveOn',
  SAVE_AN_EXTRA_100: 'SaveAnExtra100',
} as const

export type MakeTransferVariant = (typeof variants)[keyof typeof variants]

// The beat payload sends the variant as a snake_case key; map it to the value.
const VARIANT_BY_KEY: Record<string, MakeTransferVariant> = {
  save_enough_to_live_on: variants.SAVE_ENOUGH_TO_LIVE_ON,
  save_an_extra_100: variants.SAVE_AN_EXTRA_100,
}

/** Props shared by every MakeTransfer variant. Each variant reads the fields it
 *  needs directly off `slotData` — no per-variant key indirection. */
export interface MakeTransferVariantProps {
  slotData?: SaveExtraBlockData
  copy: MakeTransferCopy
}

/** Maps the payload variant key to the block's variant VALUE (defaults to save-extra). */
export const resolveMakeTransferVariant = (variant?: string | null): MakeTransferVariant =>
  VARIANT_BY_KEY[variant ?? ''] ?? variants.SAVE_AN_EXTRA_100

/** Variant-specific, localized drawer title from `insights_feed.make_transfer`. */
export const getMakeTransferDrawerTitle = (
  variant: string | null | undefined,
  copy: GlobalCopy,
): string => {
  const makeTransfer = copy.insights_feed?.make_transfer
  if (!makeTransfer) return ''
  return resolveMakeTransferVariant(variant) === variants.SAVE_ENOUGH_TO_LIVE_ON
    ? makeTransfer.save_enough_title
    : makeTransfer.save_extra_title
}

/** Coerce a payload figure (number or numeric string) to a number, else undefined. */
export const toNumber = (value?: string | number): number | undefined =>
  value === undefined || value === null || value === '' ? undefined : Number(value)
